import { useState } from "react";
import { SlidersHorizontal, Eye, EyeOff, ChevronUp, ChevronDown, AlertTriangle, BellRing, X } from "lucide-react";
import { Sheet, Pills, Delta, Bar, Empty, LineChart, signed, pctText, btnSoft } from "./ui.jsx";
import { WIDGETS, dashLayout } from "./useInvestData.js";
import { keyOf, marketOpenFor } from "../marketData.js";

const Card = ({ title, action, children, hero }) => (
  <section className={`${hero ? "card-hero ledger-bg" : "bg-card"} rounded-2xl p-4 mb-3`} aria-label={title}>
    <div className="flex items-center justify-between mb-2"><h3 className="text-xs uppercase tracking-wide text-ivory-dim">{title}</h3>{action}</div>
    {children}
  </section>
);
const Link = ({ onClick, children }) => <button onClick={onClick} className="text-xs text-brass">{children}</button>;
const ago = (ms) => { const m = Math.round((Date.now() - ms) / 60000); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`; };

function NetWorthW({ D, go }) {
  const s = D.series("1Y"), first = s.length > 1 ? s[0] : null;
  const empty = !D.nw.accounts.length && !D.nw.debts.length && !D.holdings.length;
  return (
    <Card hero title="Net worth" action={<Link onClick={() => go("networth")}>Details</Link>}>
      {empty ? <Empty title="Start with what you own" body="Add a bank account, property or holding to see your net worth.">{<button className={btnSoft} onClick={() => go("networth")}>Add accounts</button>}</Empty> : (
        <>
          <div className="v-mono text-3xl font-medium mb-1">{D.fmt(D.nw.net)}</div>
          <div className="text-xs text-ivory-dim mb-1">Assets {D.fmt(D.nw.assets)} · Debts {D.fmt(D.nw.liabilities)}</div>
          {first && <div className="text-xs mb-2"><Delta v={D.nw.net - first.n} text={`${signed(D.nw.net - first.n, D.fmt)} since ${new Date(first.t).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`} /></div>}
          {s.length > 1 && <LineChart points={s.map((p) => ({ t: p.t, v: p.n }))} fmt={D.fmt} height={64} />}
        </>
      )}
    </Card>
  );
}

function TodayW({ D }) {
  const p = D.port, open = D.holdings.some((h) => h.assetType === "crypto" || marketOpenFor(h.symbol));
  return (
    <Card title="Today's change">
      {p.dayChange == null ? <div className="text-xs text-ivory-dim">{D.holdings.length ? "Waiting for prices — daily change appears once they load." : "Add a holding to see how it moved today."}</div> : (
        <>
          <div className="flex items-baseline gap-2"><Delta className="text-2xl font-medium" v={p.dayChange} text={signed(p.dayChange, D.fmt)} /><Delta className="text-sm" v={p.dayChangePct} text={pctText(p.dayChangePct, 2)} /></div>
          <div className="text-xs text-ivory-dim mt-1">{open ? "A market is open — updating every few minutes." : "Markets closed — change since the last close."}{D.loadedAt ? ` Checked ${ago(D.loadedAt)}.` : ""}</div>
        </>
      )}
    </Card>
  );
}

function MoversW({ D }) {
  const rows = [
    ...D.holdings.filter((h) => h.sum.dayChangePct != null).map((h) => ({ k: h.id, sym: h.symbol, pct: h.sum.dayChangePct, amt: h.sum.dayChange, tag: "held" })),
    ...(D.profile.watchlist || []).map((w) => ({ k: w.id, sym: w.symbol, pct: D.quotes[keyOf(w)]?.changePct, tag: "watching" })).filter((r) => r.pct != null),
  ];
  const up = rows.filter((r) => r.pct > 0).sort((a, b) => b.pct - a.pct).slice(0, 3), down = rows.filter((r) => r.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, 3);
  const List = ({ list, label }) => (
    <div className="flex-1 min-w-0"><div className="text-xs text-ivory-dim mb-1.5">{label}</div>
      {list.length === 0 ? <div className="text-xs text-ivory-dim">None</div> : list.map((r) => (
        <div key={r.k} className="flex items-center justify-between text-xs mb-1.5 gap-2"><span className="truncate"><span className="font-semibold">{r.sym}</span> <span className="text-ivory-dim">{r.tag === "watching" ? "· watching" : ""}</span></span><Delta v={r.pct} text={pctText(r.pct, 2)} /></div>
      ))}
    </div>
  );
  return <Card title="Top movers">{rows.length === 0 ? <div className="text-xs text-ivory-dim">Movers show up once your holdings or watchlist have prices.</div> : <div className="flex gap-4"><List list={up} label="Up today" /><List list={down} label="Down today" /></div>}</Card>;
}

function PerformanceW({ D, go }) {
  const p = D.port, b = D.bench.result;
  if (!D.holdings.length) return <Card title="Performance"><div className="text-xs text-ivory-dim">Record trades in Portfolio to see your annualised return and how it compares with the market.</div></Card>;
  const gap = p.xirr != null && b?.xirr != null ? (p.xirr - b.xirr) * 100 : null;
  return (
    <Card title="Performance" action={<Link onClick={() => go("portfolio")}>Details</Link>}>
      <div className="flex gap-6 mb-2">
        <div><div className="text-xs text-ivory-dim">Annualised (XIRR)</div><Delta className="text-xl" v={p.xirr} text={p.xirr == null ? "—" : pctText(p.xirr * 100)} /></div>
        <div><div className="text-xs text-ivory-dim">{D.bench.label}</div><span className="v-mono text-xl">{D.bench.loading ? "…" : b?.xirr == null ? "—" : pctText(b.xirr * 100)}</span></div>
      </div>
      {gap != null && <div className="text-xs mb-2"><Delta v={gap} text={`${Math.abs(gap).toFixed(1)} points ${gap >= 0 ? "ahead of" : "behind"} the index`} /></div>}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div><div className="text-ivory-dim">Unrealised</div><Delta v={p.unrealised} text={signed(p.unrealised, D.fmt)} /></div>
        <div><div className="text-ivory-dim">Realised</div><Delta v={p.realised} text={signed(p.realised, D.fmt)} /></div>
        <div><div className="text-ivory-dim">Dividends</div><span className="v-mono">{D.fmt(p.dividends)}</span></div>
      </div>
    </Card>
  );
}

function AllocationW({ D, go }) {
  const g = D.groups.cls;
  return (
    <Card title="Allocation" action={<Link onClick={() => go("portfolio")}>Details</Link>}>
      {g.length === 0 ? <div className="text-xs text-ivory-dim">Nothing to split yet.</div> : (
        <>
          <div className="flex h-2.5 rounded-full overflow-hidden mb-3">{g.map((r) => <div key={r.label} style={{ width: `${r.pct}%`, background: r.color }} title={`${r.label} ${r.pct.toFixed(0)}%`} />)}</div>
          <div className="flex flex-col gap-1.5">{g.map((r) => <div key={r.label} className="flex items-center justify-between text-xs"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: r.color }} />{r.label}</span><span className="v-mono text-ivory-dim">{D.fmt(r.value)} · {r.pct.toFixed(0)}%</span></div>)}</div>
        </>
      )}
    </Card>
  );
}

export const RiskList = ({ D }) => D.warnings.map((w) => (
  <div key={w.id} className="flex gap-2.5 mb-2.5 last:mb-0">
    <AlertTriangle size={15} className="shrink-0 mt-0.5" style={{ color: "var(--rust)" }} />
    <div><div className="text-sm">{w.title}</div><div className="text-xs text-ivory-dim leading-relaxed">{w.detail}</div></div>
  </div>
));
function RiskW({ D }) {
  return (
    <Card title="Concentration check">
      {D.items.length === 0 ? <div className="text-xs text-ivory-dim">Add holdings or accounts and this checks whether too much rides on one thing.</div>
        : D.warnings.length === 0 ? <div className="text-xs text-ivory-dim leading-relaxed">Nothing flagged. No single holding, sector or region is above your limits.</div>
        : <RiskList D={D} />}
    </Card>
  );
}

function AlertsW({ D, go }) {
  const list = (D.profile.watchlist || []).flatMap((w) => (w.alerts || []).map((a) => ({ w, a })));
  const hit = list.filter((x) => x.a.hit), armed = list.length - hit.length;
  const dismiss = (wid, aid) => D.update((p) => ({ ...p, watchlist: p.watchlist.map((w) => (w.id === wid ? { ...w, alerts: w.alerts.filter((a) => a.id !== aid) } : w)) }));
  return (
    <Card title="Price alerts" action={<Link onClick={() => go("watchlist")}>Manage</Link>}>
      {list.length === 0 ? <div className="text-xs text-ivory-dim">Set a price alert on any watchlist item and it will show here when it triggers.</div> : (
        <>
          <div className="text-xs text-ivory-dim mb-2">{armed} watching · {hit.length} triggered</div>
          {hit.slice(0, 4).map(({ w, a }) => (
            <div key={a.id} className="flex items-center justify-between text-xs mb-1.5 gap-2">
              <span className="flex items-center gap-1.5 min-w-0"><BellRing size={13} style={{ color: "var(--brass)" }} /><span className="truncate"><b>{w.symbol}</b> went {a.dir} {D.fmtIn(a.price, D.quotes[keyOf(w)]?.currency)}</span></span>
              <button onClick={() => dismiss(w.id, a.id)} aria-label="Dismiss alert" className="opacity-50"><X size={13} /></button>
            </div>
          ))}
        </>
      )}
    </Card>
  );
}

const MAP = { networth: NetWorthW, today: TodayW, movers: MoversW, performance: PerformanceW, allocation: AllocationW, risk: RiskW, alerts: AlertsW };

export function Overview({ D, go }) {
  const [edit, setEdit] = useState(false);
  const { order, hidden } = D.dash;
  const shown = order.filter((i) => !hidden.includes(i));
  const move = (id, dir) => { const o = [...order], i = o.indexOf(id), j = i + dir; if (j < 0 || j >= o.length) return; [o[i], o[j]] = [o[j], o[i]]; D.saveDash({ order: o, hidden }); };
  const toggle = (id) => D.saveDash({ order, hidden: hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id] });
  const label = (id) => WIDGETS.find((w) => w[0] === id)[1];
  const lim = (k, opts) => <Pills small label={k} value={D.settings[k]} options={opts.map((v) => [v, `${v}%`])} onChange={(v) => D.setSettings({ [k]: v })} />;
  return (
    <div>
      <div className="flex justify-end mb-2"><button onClick={() => setEdit(true)} className="flex items-center gap-1.5 text-xs text-ivory-dim"><SlidersHorizontal size={13} /> Customise</button></div>
      {shown.length === 0 && <Empty title="Everything is hidden" body="Turn some cards back on in Customise." />}
      {shown.map((id) => { const W = MAP[id]; return <W key={id} D={D} go={go} />; })}
      {edit && (
        <Sheet title="Customise overview" onClose={() => setEdit(false)}>
          <p className="text-xs text-ivory-dim mb-3 leading-relaxed">Show, hide and reorder the cards on your overview.</p>
          <div className="flex flex-col gap-1.5 mb-5">
            {order.map((id, i) => (
              <div key={id} className="flex items-center gap-2 bg-card rounded-xl px-3 py-2">
                <button onClick={() => toggle(id)} aria-label={`${hidden.includes(id) ? "Show" : "Hide"} ${label(id)}`} aria-pressed={!hidden.includes(id)}>{hidden.includes(id) ? <EyeOff size={16} className="text-ivory-dim" /> : <Eye size={16} style={{ color: "var(--brass)" }} />}</button>
                <span className="flex-1 text-sm" style={{ opacity: hidden.includes(id) ? 0.5 : 1 }}>{label(id)}</span>
                <button onClick={() => move(id, -1)} disabled={i === 0} aria-label={`Move ${label(id)} up`} className="disabled:opacity-20"><ChevronUp size={16} /></button>
                <button onClick={() => move(id, 1)} disabled={i === order.length - 1} aria-label={`Move ${label(id)} down`} className="disabled:opacity-20"><ChevronDown size={16} /></button>
              </div>
            ))}
          </div>
          <div className="text-xs text-ivory-dim mb-1.5">Warn me when one holding is more than…of my investments</div>
          <div className="mb-3">{lim("maxHolding", [10, 20, 30, 40])}</div>
          <div className="text-xs text-ivory-dim mb-1.5">…or one asset is more than…of everything I own</div>
          <div className="mb-5">{lim("maxAsset", [25, 35, 50, 70])}</div>
          <button className={btnSoft + " w-full"} onClick={() => { D.saveDash(dashLayout(null)); D.setSettings({ maxHolding: 20, maxAsset: 35 }); }}>Reset to default</button>
        </Sheet>
      )}
    </div>
  );
}
